import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/design-system/atoms/modal';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { Button } from '@/components/design-system/atoms/button';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
};

const IgnoreRentModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [ignoreNote, setIgnoreNote] = useState('');

  const handleConfirm = () => {
    onConfirm(ignoreNote);
    setIgnoreNote('');
  };

  const handleClose = () => {
    setIgnoreNote('');
    onClose && onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="w-[650px] overflow-hidden rounded-lg bg-white shadow-medium">
        <div className="flex h-[102px] w-full items-center justify-between bg-R300 px-7 py-3">
          <div className="flex flex-col">
            <span className="text-lead2 font-medium text-text-primary opacity-60">
              {t('js.features.admin.rents.ignore_modal.title')}
            </span>
            <span className="text-h4 font-bold text-text-primary">
              {t('js.features.admin.rents.ignore_modal.title')}
            </span>
          </div>
          <IconSaxIcon name="eye-slash" size={70} className="opacity-20" />
        </div>
        <div className="flex w-full overflow-hidden">
          <div className="flex flex-col gap-4 bg-E30 px-4 pt-4">
            <div>
              <span className="text-body font-medium italic text-text-primary opacity-60">
                {t('js.features.admin.rents.ignore_modal.how_to_title')}
              </span>
              <div className="flex flex-col gap-3 pt-[6px]">
                <div className="flex gap-1 text-body text-text-primary">
                  <span>
                    {t('js.features.admin.rents.ignore_modal.description')}
                  </span>
                </div>
              </div>
            </div>
            <div className="h-[1px] w-full bg-E50" />
          </div>
          <div className="flex flex-col gap-6 bg-white p-7">
            <div className="flex w-[320px] flex-col gap-4">
              <div className="text-body text-text-primary">
                <div className="font-medium opacity-60">
                  {t('js.features.admin.rents.ignore_modal.note_label')}
                </div>
              </div>
              <textarea
                className="w-full rounded-lg border border-E50 bg-E30 p-3 text-body text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-R300"
                rows={3}
                value={ignoreNote}
                onChange={(e) => setIgnoreNote(e.target.value)}
                placeholder={t('js.features.admin.rents.ignore_modal.placeholder') ?? ''}
              />
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="primary"
                disabled={!ignoreNote.trim()}
                onClick={handleConfirm}
              >
                {t('js.features.admin.rents.ignore_modal.confirm_button_label') ?? ''}
              </Button>
              <Button
                variant="ghost"
                onClick={handleClose}
              >
                {t('js.features.admin.rents.ignore_modal.cancel_button_label') ?? ''}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { IgnoreRentModal };
